import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ApplicationPackage} from 'src/app/enums/application-package.enum';
import {Service} from 'src/app/enums/service.enum';
import {CustomHttpUrlEncodingCodec} from 'src/app/main/encoder';
import {PageOfContent} from 'src/app/model/page-of-content.model';
import {PlanProfileCreateRequest} from 'src/app/model/plan-profile/plan-profile-create-request.model';
import {PlanProfileConflictResponse} from 'src/app/model/plan-profile/plan-profile-conflict-response.model';
import {PlanProfileDeleteResponse} from 'src/app/model/plan-profile/plan-profile-delete-response.model';
import {PlanProfileHistory} from 'src/app/model/plan-profile/plan-profile-history.model';
import {PlanProfileUpdate} from 'src/app/model/plan-profile/plan-profile-update.model';
import {PlanProfile} from 'src/app/model/plan-profile/plan-profile.model';
import {PlanProfileAllEditRequest} from 'src/app/model/plan-profile/plan-profile-all-edit-request.model';
import {PlanProfileBulkCreateRequest} from 'src/app/model/plan-profile/plan-profile-bulk-create-request.model';
import {PlanProfileBulkDeleteRequest} from 'src/app/model/plan-profile/plan-profile-bulk-delete-request.model';
import {PlanProfileBulkDeleteResponse} from 'src/app/model/plan-profile/plan-profile-bulk-delete-response.model';
import {
  PlanProfileBulkContentEditRequest,
  PlanProfileBulkContentEditResponse,
} from 'src/app/model/plan-profile/plan-profile-bulk-content-edit.model';
import {PlanProfileSummary} from 'src/app/model/plan-profile/plan-profile-summary.model';
import {QueryParams} from 'src/app/model/query-models/query-models';
import {environment} from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PlanningService {
  readonly planingServiceUrl = environment.endPoint + ApplicationPackage.CMS_VOD + Service.PLAN_PROFILES;

  constructor(private http: HttpClient) {}

  getPlanProfileContents(planParams?: QueryParams): Observable<PageOfContent> {
    let queryParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    if (planParams.itemCount != null && planParams.itemCount != undefined) {
      queryParams = queryParams.set('itemCount', <any>planParams.itemCount);
    }
    if (planParams.contentName != null && planParams.contentName != undefined) {
      queryParams = queryParams.set('contentName', <any>planParams.contentName);
    }
    if (planParams.contentStatus != null && planParams.contentStatus != undefined) {
      queryParams = queryParams.set('contentStatus', <any>planParams.contentStatus);
    }
    if (planParams.type != null && planParams.type != undefined) {
      queryParams = queryParams.set('type', <any>planParams.type);
    }
    if (planParams.videoOperationProcessName != null && planParams.videoOperationProcessName != undefined) {
      queryParams = queryParams.set('videoOperationProcessName', <any>planParams.videoOperationProcessName);
    }
    if (planParams.platform != null && planParams.platform != undefined) {
      queryParams = queryParams.set('platform', <any>planParams.platform);
    }
    if (planParams.startTime != null && planParams.startTime != undefined) {
      queryParams = queryParams.set('startTime', <any>planParams.startTime);
    }
    if (planParams.endTime != null && planParams.endTime != undefined) {
      queryParams = queryParams.set('endTime', <any>planParams.endTime);
    }
    if (planParams.contentGroupId != null && planParams.contentGroupId != undefined) {
      queryParams = queryParams.set('contentGroupId', <any>planParams.contentGroupId);
    }
    if (planParams.serviceType != null && planParams.serviceType != undefined) {
      queryParams = queryParams.set('serviceType', <any>planParams.serviceType);
    }

    return this.http.get<PageOfContent>(this.planingServiceUrl + `/contents`, {params: queryParams});
  }

  setPlaningProfile(planprofile: PlanProfileCreateRequest): Observable<PlanProfileCreateRequest> {
    return this.http.post<PlanProfileCreateRequest>(this.planingServiceUrl, planprofile, {responseType: 'text' as 'json'});
  }

  checkConflicts(request: PlanProfileCreateRequest): Observable<PlanProfileConflictResponse[]> {
    return this.http.post<PlanProfileConflictResponse[]>(this.planingServiceUrl + `/conflicts/check`, request);
  }

  bulkCheckConflicts(request: PlanProfileBulkCreateRequest): Observable<PlanProfileConflictResponse[]> {
    return this.http.post<PlanProfileConflictResponse[]>(this.planingServiceUrl + `/bulk/conflicts/check`, request);
  }

  bulkCreatePlans(request: PlanProfileBulkCreateRequest): Observable<any> {
    return this.http.post(this.planingServiceUrl + `/bulk`, request, {responseType: 'text' as 'json'});
  }

  bulkDeletePlanProfiles(request: PlanProfileBulkDeleteRequest): Observable<PlanProfileBulkDeleteResponse> {
    return this.http.post<PlanProfileBulkDeleteResponse>(this.planingServiceUrl + `/bulk/delete`, request);
  }

  bulkEditAllByContent(
    request: PlanProfileBulkContentEditRequest,
  ): Observable<PlanProfileBulkContentEditResponse> {
    return this.http.put<PlanProfileBulkContentEditResponse>(
      this.planingServiceUrl + `/bulk/edit-all`,
      request,
    );
  }

  getPlanProfiles(id: number, type: string): Observable<PlanProfile[]> {
    let queryParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    if (type == 'Cutv') {
      queryParams = queryParams.set('catchupId', <any>id);
    } else if (type == 'Vod') {
      queryParams = queryParams.set('contentId', <any>id);
    }

    return this.http.get<PlanProfile[]>(this.planingServiceUrl, {params: queryParams});
  }

  getPlanProfileById(id: number, type?: string): Observable<PlanProfile> {
    return this.http.get<PlanProfile>(this.planingServiceUrl + `/${id}`);
  }
  countPlanProfileByLicenseIdAndContentId(licenseId: number, contentId?: number): Observable<number> {
    return this.http.get<number>(this.planingServiceUrl + `/${licenseId}/${contentId}`);
  }
  getPlanProfileByIdPromise(id: number, type?: string) {
    return this.http.get<PlanProfile>(this.planingServiceUrl + `/${id}`).toPromise();
  }

  deletePlanProfileById(id: number): Observable<PlanProfileDeleteResponse> {
    return this.http.delete<PlanProfileDeleteResponse>(this.planingServiceUrl + `/${id}`);
  }

  updatePlanProfiles(planProfile: PlanProfileUpdate): Observable<PlanProfileUpdate> {
    return this.http.put<PlanProfileUpdate>(this.planingServiceUrl, planProfile, {responseType: 'text' as 'json'});
  }
  createPlanProfiles(planprofile: PlanProfileCreateRequest): Observable<PlanProfile> {
    return this.http.post<PlanProfileCreateRequest>(this.planingServiceUrl, planprofile, {responseType: 'text' as 'json'});
  }
  //
  getContentPlanProfilesById(contentId: number): Observable<PlanProfile[]> {
    return this.http.get<PlanProfile[]>(this.planingServiceUrl + `?contentId=${contentId}`);
  }

  activateProfile(id: number): Observable<any> {
    return this.http.post(`${this.planingServiceUrl}/${id}/activate`, null, {responseType: 'text' as 'json'});
  }

  deactivateProfile(id: number): Observable<any> {
    return this.http.post<any>(`${this.planingServiceUrl}/${id}/deactivate`, null, {responseType: 'text' as 'json'});
  }

  activateAllByContent(contentId: number): Observable<any> {
    return this.http.post(`${this.planingServiceUrl}/content/${contentId}/activate-all`, null, {responseType: 'text' as 'json'});
  }

  editAllByContent(contentId: number, request: PlanProfileAllEditRequest): Observable<any> {
    return this.http.put(`${this.planingServiceUrl}/content/${contentId}/edit-all`, request, {responseType: 'text' as 'json'});
  }

  editAllByCatchup(catchupId: number, request: PlanProfileAllEditRequest): Observable<any> {
    return this.http.put(`${this.planingServiceUrl}/catchup/${catchupId}/edit-all`, request, {responseType: 'text' as 'json'});
  }

  activateAllByCatchup(catchupId: number): Observable<any> {
    return this.http.post(`${this.planingServiceUrl}/catchup/${catchupId}/activate-all`, null,
      {responseType: 'text' as 'json'});
  }

  deactivateAllByCatchup(catchupId: number): Observable<any> {
    return this.http.post(`${this.planingServiceUrl}/catchup/${catchupId}/deactivate-all`, null,
      {responseType: 'text' as 'json'});
  }

  deactivateAllByContent(contentId: number): Observable<any> {
    return this.http.post(`${this.planingServiceUrl}/content/${contentId}/deactivate-all`, null, {responseType: 'text' as 'json'});
  }

  getPlaneProfilesHistory(id: number): Observable<PlanProfileHistory[]> {
    return this.http.get<PlanProfileHistory[]>(this.planingServiceUrl + `/${id}/history`);
  }
  getPlanProfilesByActive(id: number, type: string): Observable<PlanProfile[]> {
    let queryParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    if (type == 'Cutv') {
      queryParams = queryParams.set('catchupId', <any>id);
    } else if (type == 'Vod') {
      queryParams = queryParams.set('contentId', <any>id);
    }

    return this.http.get<PlanProfile[]>(this.planingServiceUrl + `?status=ACTIVE`, {params: queryParams});
  }

  deleteByPlatform(name: string): Observable<any> {
    return this.http.delete<any>(this.planingServiceUrl + `/by-platform/deactive/${name}`, {responseType: 'text' as 'json'});
  }

  getPlanProfileSummary(contentId: number): Observable<PlanProfileSummary> {
    return this.http.get<PlanProfileSummary>(this.planingServiceUrl + '/summary?contentId=' + contentId);
  }
}
